import i18next from "./Translations";

export function getPropertyByType(tag, type) {
    let prop = Object.values(tag).find((p) => (
        p !== null && typeof p === 'object' && 'value' in p && p.type === type
    ));
    return (prop !== undefined) ? prop.value : undefined;
}

export function getPropertyByName(tag, name) {
    if (tag.hasOwnProperty(name) && tag[name].hasOwnProperty("value")) {
        return tag[name].value;
    }
    return undefined;
}

export default class TitleGenerator {
    constructor(tag, game, score) {
        this.tag = tag;
        this.game = game;
        this.score = score;
    }

    unknown() {
        let team = getPropertyByType(this.tag, "team");
        let action = this.tag.action.capitalizeFirstLetter();
        if (team) {
            return "{0}, {1}.".format(action, team);
        }
        return action;
    }

    goal() {
        let team = getPropertyByType(this.tag, "team");
        let player = getPropertyByName(this.tag, "scorer");
        let shotType = getPropertyByName(this.tag, "shot type");
        let scoreText = this.score ? (this.score.join("-") + ": ") : "";

        if (shotType === "own goal") {
            if (team && player) {
                return i18next.t("own_goal_team_player", {score: scoreText, scoring_team: team, scoring_player: player});
            } else if (team) {
                return i18next.t("own_goal_team", {score: scoreText, scoring_team: team});
            }
        }

        if (team && player) {
            return i18next.t("goal_team_player", {score: scoreText, scoring_team: team, scoring_player: player});
        } else if (team) {
            return i18next.t("goal_team_only", {score: scoreText, scoring_team: team});
        }
        return i18next.t("goal");
    }

    yellowCard() {
        let team = getPropertyByType(this.tag, "team");
        let player = getPropertyByType(this.tag, "player");

        if (team && player) {
            return i18next.t("yellow_card_team_player", {team: team, player: player});
        } else if (team) {
            return i18next.t("yellow_card_team_only", {team: team});
        }

        return i18next.t('yellow_card');
    }

    redCard() {
        let team = getPropertyByType(this.tag, "team");
        let player = getPropertyByType(this.tag, "player");

        if (team && player) {
            return i18next.t("red_card_team_player", {team: team, player: player});
        } else if (team) {
            return i18next.t("red_card_team_only", {team: team});
        }

        return i18next.t('red_card');
    }

    penalty() {
        let team = getPropertyByType(this.tag, "team");

        if (team) {
            return i18next.t("penalty_by_team", {team: team});
        }

        return i18next.t("penalty");
    }

    startPhase() {
        let phase = getPropertyByType(this.tag, "phase");

        switch (phase) {
            case "1st half":
                return i18next.t("kickoff_1st_half");
            case "2nd half":
                return i18next.t("kickoff_2nd_half");
            case "1st overtime":
                return i18next.t("kickoff_1st_overtime");
            case "2nd overtime":
                return i18next.t("kickoff_2nd_overtime");
            case "penalties":
                return i18next.t("start_of_penalties");
            default:
                return i18next.t("phase_kickoff");
        }
    }

    endPhase() {
        let phase = getPropertyByType(this.tag, "phase");

        switch (phase) {
            case "1st half":
                return i18next.t("end_1st_half");
            case "2nd half":
                return i18next.t("end_2nd_half");
            case "1st overtime":
                return i18next.t("end_1st_overtime");
            case "2nd overtime":
                return i18next.t("end_2nd_overtime");
            default:
                return i18next.t("phase_end");
        }
    }

    endOfGame() {
        if (!this.score) {
            return i18next.t("game_over");
        }

        let stadium = this.game.stadium_name;
        let winner;
        if (this.score[0] > this.score[1]) {
            winner = this.game.home_team.name;
        } else if (this.score[1] > this.score[0]) {
            winner = this.game.visiting_team.name;
        }

        if (winner && stadium) {
            return i18next.t("game_over_1",
                {
                    "winning_team": winner, "stadium": stadium,
                    "score_home": this.score[0], "score_visiting": this.score[1]
                });
        } else if (stadium) {
            return i18next.t("game_over_2",
                {
                    "stadium": stadium,
                    "score_home": this.score[0], "score_visiting": this.score[1]
                });
        } else if (winner) {
            return i18next.t("game_over_3",
                {
                    "winning_team": winner,
                    "score_home": this.score[0], "score_visiting": this.score[1]
                });
        }
        return i18next.t("game_over_4", {"score_home": this.score[0], "score_visiting": this.score[1]});
    }

    shot() {
        let team = getPropertyByType(this.tag, "team");
        let player = getPropertyByName(this.tag, "player");
        let result = getPropertyByName(this.tag, "shot result");
        let type = getPropertyByName(this.tag, "shot type");
        let onTarget = getPropertyByName(this.tag, "on target");
        let text;

        if (type === "penalty") {
            text = i18next.t("penalty_miss");
        } else if (type === "header") {
            if (result === "bar") {
                text = i18next.t("heading_on_bar");
            } else if (result === "post") {
                text = i18next.t("heading_on_post");
            } else if (onTarget === "yes") {
                text = i18next.t("heading_on_goal");
            } else if (onTarget === "no") {
                text = i18next.t("heading_wide");
            } else {
                text = i18next.t("heading");
            }
        } else {
            if (result === "bar") {
                text = i18next.t("shot_on_bar");
            } else if (result === "post") {
                text = i18next.t("shot_on_post");
            } else if (result === "blocked") {

                /* Bit different than the rest, to avoid making it seem like the player blocked the shot */
                if (player && team) {
                    return i18next.t("blocked_shot_team_player", {"team": team, "player": player});
                }
                if (team) {
                    return i18next.t("blocked_shot_team", {"team": team});
                }
                return i18next.t("blocked_shot") + ".";

            } else if (onTarget === "yes") {
                text = i18next.t("shot_on_goal");
            } else if (onTarget === "no") {
                text = i18next.t("shot_wide");
            } else {
                text = i18next.t("shot");
            }
        }

        if (player && team) {
            return i18next.t("shot_by_player", {"shot_description": text, "player": player, "team": team});
        }
        if (team) {
            return i18next.t("shot_by_team", {"shot_description": text, "team": team});
        }
        return text + ".";
    }

    offside() {
        let player = getPropertyByType(this.tag, "player");
        let team = getPropertyByType(this.tag, "team");

        if (team && player) {
            return i18next.t("offside_by_player", {"team": team, player: player});
        } else if (team) {
            return i18next.t("offside_by_team", {"team": team});
        }
        return i18next.t("offside");
    }

    corner() {
        let team = getPropertyByType(this.tag, "team");
        if (team) {
            return i18next.t("corner_by_team", {"team": team});
        }
        return i18next.t("corner");
    }

    throwIn() {
        let team = getPropertyByType(this.tag, "team");
        if (team) {
            return i18next.t("throw_in_by_team", {"team": team});
        }
        return i18next.t("throw_in");
    }

    save() {
        let team = getPropertyByType(this.tag, "team");
        if (team) {
            return i18next.t("save_by_team", {"team": team});
        }
        return i18next.t("save");
    }

    freekick() {
        let team = getPropertyByType(this.tag, "team");
        if (team) {
            return i18next.t("free_kick_by_team", {"team": team});
        }
        return i18next.t("free_kick_by_team");
    }

    injury() {
        return i18next.t("injury");
    }

    misc() {
        return i18next.t("interesting_event");
    }

    substitution() {
        let team = getPropertyByType(this.tag, "team");
        let playerIn = getPropertyByName(this.tag, "player in");
        let playerOut = getPropertyByName(this.tag, "player out");
        let text;

        if (team) {
            text = i18next.t("substitution_team", {"team": team});
        } else {
            text = i18next.t("substitution");
        }

        if (playerOut && playerIn) {
            text += " " + i18next.t("substitution_player_in_out", {"player_out": playerOut, "player_in": playerIn});
        }

        return text;
    }

    medicalTreatment() {
        let player = getPropertyByType(this.tag, "player");
        if (player) {
            return i18next.t("medical_assistance_player", {"player": player});
        }
        return i18next.t("medical_assistance");
    }
}


// WEBPACK FOOTER //
// ./src/js/include/TitleGenerator.js